import React, { useState } from 'react';
import _orderBy from 'lodash/orderBy';
import { withRouter } from 'react-router-dom';
import { useQuery } from 'react-query';

import { withStyles } from '@material-ui/core/styles';

import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableSortLabel from '@material-ui/core/TableSortLabel';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTablePagination from '@material-ui/core/TablePagination';
import MuiTableFooter from '@material-ui/core/TableFooter';
import MuiBox from '@material-ui/core/Box';
import MuiGrid from '@material-ui/core/Grid';

import * as API from 'api';

import Row from './row';

const { getAll } = API.AffiliatePayments();

const styles = () => ({
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

const createRowData = row => {
  return {
    id: row?.id,
    user_group_type: row?.user ? 'Person' : 'Label',
    user_group_name: row?.user ? row?.user?.name : row?.group?.name,
    account_email: row?.user ? row?.user?.email : row?.group?.email,
    stripe_account_id: row.stripe_account_id,
    sum_post_recoup_usd: row.balance,
    referral_amount_sortable: parseFloat(0),
    external_transfer_id: null,
    transferred_amount: 0,
    untransferred_amount: 0,
    can_close: false,
    is_closed: false,
    closed_at: null,
    can_execute_external_transfer: false,
    transferred_at: null,
    external_transfer_method: 'Stripe',
    deducted_fee_amount: 0,
  };
};

const TableAffiliatePayments = props => {
  const { classes, onEditRow, showIssues } = props;

  const [editingId, setEditingId] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [isClosing, setIsClosing] = useState([]);

  const [params, setParams] = useState({ page: 0, rowsPerPage: 250 });
  const [affiliatePayments, setAffiliatePayments] = useState([]);
  const [total, setTotal] = useState(0);

  const state = showIssues ? 'open' : 'executed';

  const { isLoading, refetch } = useQuery(
    ['allAffiliatePayments', params.page, params.rowsPerPage, state],
    () => getAll({ params: { ...params, state } }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 0,
      onSuccess: data => {
        setTotal(data.total);
        setAffiliatePayments(data.results);
      },
    }
  );

  function handleChangePage(_event, newPage) {
    setParams({ ...params, page: newPage });
  }

  function handleChangeRowsPerPage({ target: { value: newRowsPerPage } }) {
    setParams({ ...params, page: 0, rowsPerPage: newRowsPerPage });
  }

  const headCells = [
    {
      id: 'id',
      numeric: true,
      label: 'id',
    },
    {
      id: 'user_group_type',
      numeric: false,
      label: 'Type',
    },
    {
      id: 'user_group_name',
      numeric: false,
      label: 'Account',
    },
    {
      id: 'external_transfer_method',
      numeric: false,
      label: 'Method',
    },
    {
      id: 'referral_amount_sortable',
      numeric: true,
      label: 'Amount',
    },
    {
      id: 'status',
      numeric: true,
      label: 'Status',
    },
  ];

  const dataAffiliatePayments = affiliatePayments?.map(row => createRowData(row)) || [];

  const handleRequestSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading)
    return (
      <MuiTableContainer component={MuiPaper}>
        <div className={classes.loadWrapper}>
          <MuiCircularProgress />
        </div>
      </MuiTableContainer>
    );

  return (
    <MuiTableContainer component={MuiPaper}>
      <MuiGrid container spacing={1}>
        <MuiGrid item xs={12}>
          <MuiBox display="flex" justifyContent="flex-end">
            <MuiTablePagination
              rowsPerPageOptions={[250, 500, 1000]}
              rowsPerPage={params.rowsPerPage}
              count={total}
              page={params.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MuiBox>
        </MuiGrid>
      </MuiGrid>
      <MuiTable aria-label="collapsible table" size="small">
        <MuiTableHead>
          <MuiTableRow>
            {headCells.map(headCell => (
              <MuiTableCell
                key={headCell.id}
                align={headCell.numeric ? 'right' : 'left'}
                sortDirection={orderBy === headCell.id ? order : false}>
                <MuiTableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={_ => handleRequestSort(headCell.id)}>
                  {headCell.label}
                </MuiTableSortLabel>
              </MuiTableCell>
            ))}
            <MuiTableCell></MuiTableCell>
          </MuiTableRow>
        </MuiTableHead>
        <MuiTableBody>
          {_orderBy(
            dataAffiliatePayments.filter(row => !showIssues || row.external_transfer_id === null),
            [orderBy],
            [order]
          ).map(row => (
            <Row
              key={row.id}
              row={row}
              editing={editingId === row.id}
              onEdit={onEditRow}
              isClosing={isClosing.includes(row.id)}
            />
          ))}
        </MuiTableBody>
        <MuiTableFooter>
          <MuiTableRow>
            <MuiTableCell colSpan={6}>
              <MuiBox display="flex" justifyContent="flex-end">
                <MuiTablePagination
                  rowsPerPageOptions={[250, 500, 1000]}
                  rowsPerPage={params.rowsPerPage}
                  count={total}
                  page={params.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </MuiBox>
            </MuiTableCell>
          </MuiTableRow>
        </MuiTableFooter>
      </MuiTable>
    </MuiTableContainer>
  );
};

export default withStyles(styles)(withRouter(TableAffiliatePayments));
